import React from 'react';
import type { SalesMarket } from '@/hooks/useSalesMarkets';

/**
 * Filter markets by case-insensitive partial matching against
 * display_name, market_code, and synonyms.
 *
 * Returns all markets when query is empty or whitespace-only.
 */
export function filterMarkets(markets: SalesMarket[], query: string): SalesMarket[] {
  const q = query.trim().toLowerCase();
  if (!q) return markets;

  // Score each market: exact synonym/code match = 1 (highest), partial synonym/code = 2, name match = 3
  const scored: { market: SalesMarket; score: number }[] = [];

  for (const m of markets) {
    const codeMatch = m.market_code.toLowerCase() === q;
    const synonymMatch = m.synonyms
      ? m.synonyms.split(',').some((s) => s.trim().toLowerCase() === q)
      : false;
    const nameMatch = m.display_name.toLowerCase().includes(q);
    const codePartialMatch = m.market_code.toLowerCase().includes(q);
    const synonymPartialMatch = m.synonyms
      ? m.synonyms.split(',').some((s) => s.trim().toLowerCase().includes(q))
      : false;

    if (codeMatch || synonymMatch) {
      scored.push({ market: m, score: 1 }); // Exact synonym/code match — top priority
    } else if (synonymPartialMatch || codePartialMatch) {
      scored.push({ market: m, score: 2 }); // Partial synonym/code match
    } else if (nameMatch) {
      scored.push({ market: m, score: 3 }); // Name contains query
    }
  }

  // Sort by score (lower = better), then alphabetically by display_name
  scored.sort((a, b) => {
    if (a.score !== b.score) return a.score - b.score;
    return a.market.display_name.localeCompare(b.market.display_name);
  });

  return scored.map((s) => s.market);
}

/**
 * Resolve a market text value (from spreadsheet paste/upload) to a market_code.
 * Tries exact match against display_name, market_name, market_code, and synonyms (case-insensitive).
 * Returns the market_code if found, or null if no match.
 */
export function resolveMarketCode(markets: SalesMarket[], text: string): string | null {
  const q = text.trim().toLowerCase();
  if (!q) return null;

  for (const m of markets) {
    // Exact match on display_name
    if (m.display_name.toLowerCase() === q) return m.market_code;
    // Exact match on market_name
    if (m.market_name.toLowerCase() === q) return m.market_code;
    // Exact match on market_code
    if (m.market_code.toLowerCase() === q) return m.market_code;
    // Exact match on any synonym
    if (m.synonyms) {
      const syns = m.synonyms.split(',').map((s) => s.trim().toLowerCase());
      if (syns.includes(q)) return m.market_code;
    }
  }

  return null;
}

/**
 * Highlight the first occurrence of `query` within `text` by wrapping
 * the matched portion in a <strong> tag. Returns the original text
 * as a plain string when there is no match or query is empty.
 */
export function highlightMatch(text: string, query: string): React.ReactNode {
  const q = query.trim();
  if (!q) return text;

  const lowerText = text.toLowerCase();
  const lowerQuery = q.toLowerCase();
  const idx = lowerText.indexOf(lowerQuery);

  if (idx === -1) return text;

  const before = text.slice(0, idx);
  const match = text.slice(idx, idx + q.length);
  const after = text.slice(idx + q.length);

  return React.createElement(React.Fragment, null,
    before,
    React.createElement('strong', null, match),
    after,
  );
}
